import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core'; // Import AfterViewInit
import jsonData from '../../../../assets/json/transactions.json';
import { ButtonClickService } from 'src/app/shared/services/button-click.service';
import { WalletSummaryService } from 'src/app/shared/services/api/wallet-summary.service';
import { WalletSummary } from 'src/app/shared/models/wallet-summary.interface';
import { TransactionHistoryService } from 'src/app/shared/services/api/transaction-history.service';
import { CashEarn } from 'src/app/shared/models/transaction-history.interface';
import { CashCommon } from 'src/app/shared/models/CashCommon.interface';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { transition } from '@angular/animations';
import { FileExportDialogComponent } from 'src/app/shared/dialog/file-export-dialog/file-export-dialog.component';
import { ExportService } from 'src/app/shared/services/export.service';
import { MatDialog } from '@angular/material/dialog';


@Component({
  selector: 'app-all-cash',
  templateUrl: './all-cash.component.html',
  styleUrls: ['./all-cash.component.scss']
})
export class AllCashComponent implements OnInit, AfterViewInit { // Implement AfterViewInit

  dataSource = new MatTableDataSource<CashCommon>([]);
  displayedColumns: string[] = [];
  autoPay: number = 0;

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort; // Changed static to true, as it's needed in AfterViewInit
  selection = new SelectionModel<CashCommon>(true, []);
  searchValue: string = '';

  rewardType: string = 'all'; // Default to 'all'
  walletID: string = '';
  walletRole: string = ''

  cashEligible = false;
  rewardEligible = false;

  cashEarnedList: any[] = [];
  earnCategoryList: any[] = [];
  detailsList: any[] = [];
  dateList: any[] = [];
  referenceNumberList: any[] = [];
  dataLoaded = false;

  constructor(
    public buttonClick: ButtonClickService,
    private walletSummaryService: WalletSummaryService,
    private transactionHistoryService: TransactionHistoryService,
    public exportService: ExportService, public dialog: MatDialog) { }

  transactions = jsonData;
  walletSummary: WalletSummary | null = null;

  selectAll(event: Event) {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    this.transactions.forEach(transaction => {
      transaction.selected = isChecked;
    });
  }

  goToRedeem() {
    this.buttonClick.goToRedeem();
  }

  goToTransferBalance() {
    this.buttonClick.goToTransferBalance();
  }

  ngOnInit(): void {
    this.walletSummaryService.walletSummary$.subscribe(summary => {
      if (summary && summary.wallet) {
        this.walletID = summary.wallet.walletID;
        this.walletRole = 'admin';
        this.cashEligible = summary?.wallet?.cashEligible === "True";
        this.rewardEligible = summary?.wallet?.rewardEligible === "True";

        const isDWAutoPayValue = Number(summary.wallet.isDWAutoPay);
        this.autoPay = isDWAutoPayValue;

        this.walletSummary = summary;
        this.dataLoaded = true;

        // Fetch cash data on init, which will handle the default 'all' type
        this.fetchCash();
      }
    });

    this.dataSource.filterPredicate = (data: any, filter: string) => {
      // Adjusted to check the 'date' property which is present in 'all' type data
      const dateString = new Date(data.date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
      const amountString = data.amount ? data.amount.toString().toLowerCase() : '';
      const transactionTypeString = data.transactionType ? data.transactionType.toLowerCase() : '';
      const detailString = data.detail ? data.detail.toLowerCase() : '';
      const referenceNumberString = data.referenceNumber ? data.referenceNumber.toString().toLowerCase() : '';
      const accountString = data.account ? data.account.toLowerCase() : '';
      const statusString = data.status ? data.status.toLowerCase() : '';


      return dateString.includes(filter) ||
             amountString.includes(filter) ||
             transactionTypeString.includes(filter) ||
             detailString.includes(filter) ||
             referenceNumberString.includes(filter) ||
             accountString.includes(filter) ||
             statusString.includes(filter);
    };


    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'earnDate':
        case 'spendDate':
        case 'date': // Add 'date' case for the 'all' tab
          return new Date(item[property]);
        default:
          // Ensure non-date properties are returned as-is for sorting,
          // converting to lowercase for string comparison if needed.
          if (typeof item[property] === 'string') {
            return item[property].toLowerCase();
          }
          return item[property];
      }
    };
  }

  // ---

  ngAfterViewInit(): void {
    // Assign paginator and sort AFTER view initialization to ensure they are available
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // Set default sort for 'all' tab after data is loaded and dataSource is set
    // We need to wait for the data to be fetched and assigned to dataSource
    // before we can effectively apply the sort to it.
    // The fetchCash() method is called inside the subscription, so we ensure sort is applied AFTER that.
    this.sort.sortChange.subscribe(() => this.paginator.firstPage()); // Reset pagination on sort change
    
    // Initial sort for the "All" tab when the component loads
    if (this.rewardType === 'all' && this.dataSource.data.length > 0) {
      this.sort.sort({ id: 'date', start: 'desc', disableClear: true });
    }
  }

  // ---

  fetchCash(): void {
    this.transactionHistoryService.fetchTransactionHistory(this.walletID, this.walletRole, 'cash').subscribe(
      (data) => {
        if (this.rewardType === 'cashEarn') {
          const sortedData = this.transformData(data.walletTransactionsList.cash.cashEarn, true, false);
          this.dataSource.data = sortedData; // Assign data directly, MatSort will handle sorting now
          this.displayedColumns = ['select', 'earnDate', 'cashEarned', 'earnCategory', 'earnIdentifierDescription', 'earnIdentifier', 'trackingNumber'];
          
          // Apply sort specifically for cashEarn (if needed after initial load)
          this.sort.sort({ id: 'earnDate', start: 'desc', disableClear: true });

        } else if (this.rewardType === 'cashSpend') {
          const sortedData = this.transformData(data.walletTransactionsList.cash.cashSpend, false, false);
          this.dataSource.data = sortedData; // Assign data directly
          this.displayedColumns = ['select', 'spendDate', 'spendAmount', 'spendCategory', 'spendIdentifierDescription', 'spendAccountAlias', 'spendAccountBankName'];

          // Apply sort specifically for cashSpend (if needed after initial load)
          this.sort.sort({ id: 'spendDate', start: 'desc', disableClear: true });

        } else if (this.rewardType === 'all') {
          const cashEarn: any[] = data.walletTransactionsList.cash.cashEarn;
          const cashSpend: any[] = data.walletTransactionsList.cash.cashSpend;

          const consolidation = [...cashEarn, ...cashSpend];
          const transformedConsolidation = this.transformData(consolidation, false, true);

          this.dataSource.data = transformedConsolidation; // Assign data directly
          this.displayedColumns = ['select', 'date', 'amount', 'transactionType', 'detail', 'referenceNumber', 'account', 'status'];

          // Apply default sort for 'all' tab when data is fetched
          this.sort.sort({ id: 'date', start: 'desc', disableClear: true });
        }

        // Re-assign paginator and sort after data updates
        // This ensures MatSort and MatPaginator are properly linked to the new dataSource.data
        if (this.dataSource.paginator) {
          this.dataSource.paginator.firstPage();
        }
        if (this.dataSource.sort) {
          this.dataSource.sort.active = this.rewardType === 'all' ? 'date' : (this.rewardType === 'cashEarn' ? 'earnDate' : 'spendDate');
          this.dataSource.sort.direction = 'desc';
          this.dataSource.sort.sortChange.emit({active: this.dataSource.sort.active, direction: this.dataSource.sort.direction});
        }

      },
      (error) => {
        console.error('Error fetching cash transactions:', error);
      }
    );
  }


  transformData(data: any[], isEarn: boolean, isConsolidated: boolean): any[] {
    if (isConsolidated) {
      return data.map((item) => ({
        date: item.spendDate ? item.spendDate : item.earnDate,
        amount: item.spendAmount ? item.spendAmount : item.cashEarned,
        transactionType: item.cashEarned ? 'Incoming' : 'Outgoing',
        detail: item.cashEarned ? item.earnCategoryDescription === 'CashEarnClaimsPaid' ? 'Claims' : item.earnCategoryDescription
          : item.spendIdentifierDescription === 'InvoiceNumber' ?
            'Invoice Redemption' : item.spendIdentifierDescription ===
              'PayableReferenceNumber' ? 'Cash Transfer '
                : item.spendIdentifierDescription,
        transferType: item.modeOfTransfer?.toLowerCase() === 'rtp' ? '- Instant' : '',
        referenceNumber: item.spendIdentifier && item.spendIdentifierDescription
          === 'InvoiceNumber' ? 'INV. ' + item.spendIdentifier :
          item.spendIdentifier ? item.spendIdentifier
            : item.earnIdentifier,
        account: item.spendDate ? item.spendAccountRedactedDescription?.toLowerCase() === 'ach' ? item.spendAccountBankName + ' ****' +
          item.spendAccountRedacted : item.spendAccountRedactedDescription?.toLowerCase() === 'zelle' ? item.spendAccountRedactedDescription + '- ****' + item.spendAccountRedacted : 'N/A' : 'N/A',
        status: item.status ? item.status : 'N/A'
      }));
    }
    else {
      return data;
    }
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  applySearch(searchValue: string) {
    const formattedSearchValue = searchValue.trim().toLowerCase();
    this.dataSource.filter = formattedSearchValue;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onRewardTypeChange(rewardType: string): void {
    // This method is called by the (change) event on the radio buttons
    // The fetchCash() method is already called in the (change) handler in HTML
    // this.fetchCash() will handle setting the correct displayedColumns and sorting
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: CashCommon): string {
    if (!row) return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.date + row.amount}`;
  }

  exportData() {
    const dialogRef = this.dialog.open(FileExportDialogComponent, {
      disableClose: true,
      hasBackdrop: true,
      data: { transactiontype: this.rewardType, source: "cash" } // Pass the file type as data
    });
  }

  // Removed getDisplayedColumns() as it's handled within fetchCash() now
  // getDisplayedColumns(): string[] {
  //   if (this.rewardType === 'cashEarn') {
  //     return ['select', 'earnDate', 'cashEarned', 'earnCategory', 'earnIdentifier', 'earnIdentifierDescription'];
  //   } else if (this.rewardType === 'cashSpend') {
  //     return ['select', 'spendDate', 'spendAmount', 'spendCategory', 'spendIdentifierDescription'];
  //   } else {
  //     // Default or error case
  //     return [];
  //   }
  // }
}